import {PlayerColor} from '@colonist/game-states'
import {RankedDivisionTypes, RankedSubDivisionTypes} from '@colonist/leaderboard-types'
import {LobbyImage} from '@colonist/types'
import {escapeRegExp} from 'lodash'

export class UIImagePathController {
    static readonly STATIC_LOCALE_PATH = '/dist/locales/'
    private static readonly STATIC_IMAGE_PATH = '/dist/images/'
    private static readonly STATIC_SOUND_PATH = '/dist/sounds/'
    private static readonly STATIC_RANKED_DIVISION_PATH = 'ranking_divisions'
    private static readonly DISCORD_PROXY_PATH = '/.proxy'
    private static readonly matchDiscordPrefix = new RegExp(`^${escapeRegExp(this.DISCORD_PROXY_PATH)}`)
    private static versionNumber: number
    // Andre Nov 9 2023
    // Do not change the next line unless SetDistRevisions.ts is also updated. the empty object will be replaced with the revisions of all assets
    private static readonly fileRevisions: Record<string, string> = {}

    static setVersionNumber(versionNumber: number) {
        this.versionNumber = versionNumber
    }

    static addRevision(fullFilename: string): string {
        const lookupFilename = fullFilename.replace(this.matchDiscordPrefix, '')

        if(this.fileRevisions[lookupFilename] == undefined) {
            return `${fullFilename}?v=${this.versionNumber}`
        }

        return `${fullFilename}?rev=${this.fileRevisions[lookupFilename]}`
    }

    static getImagePath(file: string, isDiscord: boolean = false): string {
        const pathPrefix = isDiscord ? this.DISCORD_PROXY_PATH : ''
        return this.addRevision(`${pathPrefix}${this.STATIC_IMAGE_PATH}${file}`)
    }

    static getSoundPath(file: string, isDiscord: boolean = false): string {
        const pathPrefix = isDiscord ? this.DISCORD_PROXY_PATH : ''
        return this.addRevision(`${pathPrefix}${this.STATIC_SOUND_PATH}${file}`)
    }

    static getLobbyImagePath(img: LobbyImage, isDiscord: boolean = false): string {
        const pathPrefix = isDiscord ? this.DISCORD_PROXY_PATH : ''
        return pathPrefix + this.getImagePath(this.getImgPath(img))
    }

    static getLocalePath(file: string, isDiscord: boolean = false): string {
        const pathPrefix = isDiscord ? this.DISCORD_PROXY_PATH : ''
        return this.addRevision(`${pathPrefix}${this.STATIC_LOCALE_PATH}${file}`)
    }

    static getRankedDivisionImage(division: RankedDivisionTypes): LobbyImage {
        switch(division) {
            case RankedDivisionTypes.Bronze: return LobbyImage.RankBronze
            case RankedDivisionTypes.Silver: return LobbyImage.RankSilver
            case RankedDivisionTypes.Gold: return LobbyImage.RankGold
            case RankedDivisionTypes.Platinum: return LobbyImage.RankPlatinum
            case RankedDivisionTypes.Diamond: return LobbyImage.RankDiamond
            case RankedDivisionTypes.Master: return LobbyImage.RankMaster
            case RankedDivisionTypes.Grandmaster: return LobbyImage.RankGrandmaster
            case RankedDivisionTypes.None: return LobbyImage.XMark
        }
    }

    static getRankedSubdivisionImage(subdivision: RankedSubDivisionTypes): LobbyImage | undefined {
        if(subdivision === RankedSubDivisionTypes.None) return

        switch(subdivision) {
            case RankedSubDivisionTypes.I: return LobbyImage.RankSubdivisionI
            case RankedSubDivisionTypes.II: return LobbyImage.RankSubdivisionII
            case RankedSubDivisionTypes.III: return LobbyImage.RankSubdivisionIII
            case RankedSubDivisionTypes.IV: return LobbyImage.RankSubdivisionIV
            case RankedSubDivisionTypes.V: return LobbyImage.RankSubdivisionV
        }
    }

    static getPlayerColorBuildings(color: PlayerColor): {road: LobbyImage, settlement: LobbyImage, city: LobbyImage} {
        switch(color) {
            case PlayerColor.Red: return {road: LobbyImage.RoadRed, settlement: LobbyImage.SettlementRed, city: LobbyImage.CityRed}
            case PlayerColor.Blue: return {road: LobbyImage.RoadBlue, settlement: LobbyImage.SettlementBlue, city: LobbyImage.CityBlue}
            case PlayerColor.Green: return {road: LobbyImage.RoadGreen, settlement: LobbyImage.SettlementGreen, city: LobbyImage.CityGreen}
            case PlayerColor.Orange: return {road: LobbyImage.RoadOrange, settlement: LobbyImage.SettlementOrange, city: LobbyImage.CityOrange}
            case PlayerColor.Black: return {road: LobbyImage.RoadBlack, settlement: LobbyImage.SettlementBlack, city: LobbyImage.CityBlack}
            case PlayerColor.Silver: return {road: LobbyImage.RoadSilver, settlement: LobbyImage.SettlementSilver, city: LobbyImage.CitySilver}
            case PlayerColor.Gold: return {road: LobbyImage.RoadGold, settlement: LobbyImage.SettlementGold, city: LobbyImage.CityGold}
            case PlayerColor.White: return {road: LobbyImage.RoadWhite, settlement: LobbyImage.SettlementWhite, city: LobbyImage.CityWhite}
            case PlayerColor.Purple: return {road: LobbyImage.RoadPurple, settlement: LobbyImage.SettlementPurple, city: LobbyImage.CityPurple}
            case PlayerColor.MysticBlue: return {road: LobbyImage.RoadMysticBlue, settlement: LobbyImage.SettlementMysticBlue, city: LobbyImage.CityMysticBlue}
            case PlayerColor.Bronze: return {road: LobbyImage.RoadBronze, settlement: LobbyImage.SettlementBronze, city: LobbyImage.CityBronze}
            case PlayerColor.Pink: return {road: LobbyImage.RoadPink, settlement: LobbyImage.SettlementPink, city: LobbyImage.CityPink}
            case PlayerColor.None: break
        }

        return {road: LobbyImage.CheckMark, settlement: LobbyImage.CheckMark, city: LobbyImage.CheckMark}
    }

    static getImgPath(img: LobbyImage): string {
        switch(img) {
            case LobbyImage.Pencil: return 'icon_pencil.svg'
            case LobbyImage.Bot: return 'icon_bot.svg'
            case LobbyImage.CheckMark: return 'icon_check.svg'
            case LobbyImage.XMark: return 'icon_x.svg'
            case LobbyImage.Logo: return 'logo.png'
            case LobbyImage.Lightning: return 'icon_lightning.svg'
            case LobbyImage.Plus: return 'icon_plus.svg'
            case LobbyImage.PlusStore: return 'icon_plus_store.svg'
            case LobbyImage.Mail: return 'icon_mail.svg'
            case LobbyImage.Admin: return 'icon_admin.svg'
            case LobbyImage.Moderator: return 'moderator.svg'
            case LobbyImage.User: return 'icon_player_loggedin.svg'
            case LobbyImage.Guest: return 'icon_player.svg'
            case LobbyImage.Star: return 'icon_star.svg'
            case LobbyImage.Players: return 'icon_players.svg'
            case LobbyImage.ReplayIcon: return 'icon_replay.svg'
            case LobbyImage.QuestionMark: return 'icon_question_mark.svg'
            case LobbyImage.InfoIcon: return 'icon_info.svg'
            case LobbyImage.DangerIcon: return 'icon_danger.svg'
            case LobbyImage.KarmaWhite: return 'karma_white.svg'
            case LobbyImage.KarmaRed: return 'karma_red.svg'
            case LobbyImage.KarmaOrange: return 'karma_orange.svg'
            case LobbyImage.KarmaGreen: return 'karma_green.svg'
            case LobbyImage.DisableAdBlock: return 'disable_abblock.png'
            case LobbyImage.ColonistCoin: return 'icon_colonist_coin.svg'
            case LobbyImage.Rulebook: return 'icon_book.svg'
            case LobbyImage.BankIcon: return 'icon_bank.svg'
            case LobbyImage.FullScreenExpand: return 'icon_fullscreen_expand.svg'
            case LobbyImage.FullScreenContract: return 'icon_fullscreen_contract.svg'
            case LobbyImage.FullScreenGif: return 'gif_fullscreen.gif'
            case LobbyImage.LeftArrow: return 'icon_arrow.svg'
            case LobbyImage.Audio: return 'icon_audio.svg'
            case LobbyImage.Eye: return 'icon_eye.svg'
            case LobbyImage.SendArrow: return 'icon_send.svg'
            case LobbyImage.Settings: return 'icon_settings.svg'
            case LobbyImage.Chat: return 'icon_chat.svg'
            case LobbyImage.Log: return 'icon_log.svg'
            case LobbyImage.MatchmakingSearch: return 'icon_mm_search.svg'
            case LobbyImage.Lock: return 'icon_lock.svg'
            case LobbyImage.LockOutlined: return 'icon_lock_outlined.svg'
            case LobbyImage.LockSlanted: return 'icon_lock_slanted.svg'
            case LobbyImage.Share: return 'icon_share.svg'
            case LobbyImage.NoAds: return 'icon_no_ads.svg'
            case LobbyImage.Colors: return 'icon_colors.svg'
            case LobbyImage.Avatars: return 'store_icon_avatar.png'
            case LobbyImage.Maps: return 'store_icon_maps.png'
            case LobbyImage.Expansions: return 'store_icon_expansions.png'
            case LobbyImage.AllColors: return 'store_icon_colors.png'

            case LobbyImage.MapDiamondPreview: return 'map_diamond_preview.png'
            case LobbyImage.MapIstanbulPreview: return 'map_istanbul_preview.png'
            case LobbyImage.MapLakesPreview: return 'map_lakes_preview.png'
            case LobbyImage.MapPondPreview: return 'map_pond_preview.png'
            case LobbyImage.MapGearPreview: return 'map_gear_preview.png'
            case LobbyImage.MapUSAPreview: return 'map_usa_preview.png'
            case LobbyImage.MapTwirlPreview: return 'map_twirl_preview.png'
            case LobbyImage.MapEarthPreview: return 'map_earth_preview.png'
            case LobbyImage.MapUKPreview: return 'map_uk_preview.png'
            case LobbyImage.MapShuffleBoardPreview: return 'map_shuffleboard_preview.png'
            case LobbyImage.MapPangaeaPreview: return 'map_pangaea_preview.png'
            case LobbyImage.MapSpinnerPreview: return 'map_spinner_preview.png'
            case LobbyImage.MapCyclopsPreview: return 'map_cyclops_preview.png'
            case LobbyImage.MapRandomBasePreview: return 'map_random_base_preview.png'
            case LobbyImage.MapClassic4PPreview: return 'map_base_4p_preview.png'
            case LobbyImage.MapRomaniaPreview: return 'map_romania_preview.png'
            case LobbyImage.MapHourglassPreview: return 'map_hourglass_preview.png'
            case LobbyImage.MapHourglassXLPreview: return 'map_hourglassxl_preview.png'
            case LobbyImage.MapSnowflakePreview: return 'map_snowflake_preview.png'
            case LobbyImage.MapMiniBasePreview: return 'map_mini_base_preview.png'
            case LobbyImage.MapAustraliaPreview: return 'map_australia_preview.png'
            case LobbyImage.MapCirclePreview: return 'map_circle_preview.png'
            case LobbyImage.MapDuelPreview: return 'map_duel_preview.png'
            case LobbyImage.MapMonopolyPreview: return 'map_monopoly_preview.png'
            case LobbyImage.MapKingOfTheHillPreview: return 'map_king_of_the_hill_preview.png'
            case LobbyImage.MapEuropePreview: return 'map_europe_preview.png'

            case LobbyImage.MapMountainOfRichesPreview: return 'map_mountain_of_riches_preview.png'
            case LobbyImage.MapDoggoPreview: return 'map_doggo_preview.png'
            case LobbyImage.MapTradeIslandsPreview : return 'map_trade_islands_preview.png'
            case LobbyImage.MapTurningTidesPreview : return 'map_turning_tides_preview.png'
            case LobbyImage.MapBlackForestPreview: return 'map_blackforest_preview.png'
            case LobbyImage.MapVolcanoPreview: return 'map_volcano_preview.png'
            case LobbyImage.MapKitePreview: return 'map_kite_preview.png'
            case LobbyImage.MapVietnamPreview: return 'map_vietnam_preview.png'
            case LobbyImage.MapTwoIslesPreview: return 'map_two_isles_preview.png'
            case LobbyImage.MapJellyFishPreview: return 'map_jellyfish_preview.png'
            case LobbyImage.MapGoldRushPreview: return 'map_goldrush_preview.png'
            case LobbyImage.MapStripesPreview: return 'map_stripes_preview.png'
            case LobbyImage.MapMonopolyIslandsPreview: return 'map_monopoly_islands_preview.png'

            case LobbyImage.MapClassic56PPreview: return 'map_5_6_player_preview.png'
            case LobbyImage.MapClassic78PPreview: return 'map_7_8_player_preview.png'

            case LobbyImage.MapSS1HeadingForNewShores3PPreview: return 'map_ss1_3p_preview.png'
            case LobbyImage.MapSS1HeadingForNewShores4PPreview: return 'map_ss1_4p_preview.png'
            case LobbyImage.MapSS1HeadingForNewShores56PPreview: return 'map_ss1_6p_preview.png'
            case LobbyImage.MapSS2FourIslands3PPreview: return 'map_ss2_3p_preview.png'
            case LobbyImage.MapSS2FourIslands4PPreview: return 'map_ss2_4p_preview.png'
            case LobbyImage.MapSS2SixIslands56PPreview: return 'map_ss2_6p_preview.png'
            case LobbyImage.MapSS3FogIslands3PPreview: return 'map_ss3_3p_preview.png'
            case LobbyImage.MapSS3FogIslands4PPreview: return 'map_ss3_4p_preview.png'
            case LobbyImage.MapSS3FogIslands56PPreview: return 'map_ss3_6p_preview.png'
            case LobbyImage.MapSS4ThroughTheDesert3PPreview: return 'map_ss4_3p_preview.png'
            case LobbyImage.MapSS4ThroughTheDesert4PPreview: return 'map_ss4_4p_preview.png'
            case LobbyImage.MapSS4ThroughTheDesert56PPreview: return 'map_ss4_6p_preview.png'

            case LobbyImage.GameMode1v1: return 'icon_gamemode_1v1.svg'
            case LobbyImage.GameModeBase:
            case LobbyImage.GameModeBasePreview: return 'game_mode_icons/base.svg'
            case LobbyImage.GameModeBase56P: return 'game_mode_icons/56player.svg'
            case LobbyImage.GameModeBase78P: return 'game_mode_icons/78player.svg'
            case LobbyImage.GameModeSeafarers: return 'game_mode_icons/seafarers.svg'
            case LobbyImage.GameModeSeafarers56P: return 'game_mode_icons/seafarers_56player.svg'
            case LobbyImage.GameModeCnK: return 'game_mode_icons/cities_and_knights.svg'
            case LobbyImage.GameModeCitiesAndKnights56P: return 'game_mode_icons/cities_and_knights_56player.svg'
            case LobbyImage.GameModeCnKSeafarers: return 'game_mode_icons/seafarers_cities_and_knights.svg'
            case LobbyImage.GameModeSeafarersCitiesAndKnights56P: return 'game_mode_icons/seafarers_cities_and_knights_56player.svg'

            case LobbyImage.SettlerPack: return 'pack_settler.svg'
            case LobbyImage.ColonistPack: return 'pack_colonist.svg'
            case LobbyImage.FounderPack: return 'pack_founder.svg'

            case LobbyImage.RoadBronze: return 'road_bronze.svg'
            case LobbyImage.SettlementBronze: return 'settlement_bronze.svg'
            case LobbyImage.CityBronze: return 'city_bronze.svg'

            case LobbyImage.Bank: return 'bank.svg'
            case LobbyImage.Robber: return 'icon_robber.svg'
            case LobbyImage.Dice6: return 'dice_6.svg'
            case LobbyImage.PrivateGame: return 'icon_sunglasses.svg'
            case LobbyImage.HideBankCards: return 'icon_hide_bank_card.svg'

            case LobbyImage.StoreItemExpansionClassic56P: return 'store/store_item_56player.png'
            case LobbyImage.StoreItemExpansionClassic78P: return 'store/store_item_78player.png'
            case LobbyImage.StoreItemSeafarers4P: return 'store/store_item_seafarers.png'
            case LobbyImage.StoreItemSeafarers56P: return 'store/store_item_seafarers_56player.png'
            case LobbyImage.StoreItemCitiesAndKnights4P: return 'store/store_item_cities_and_knights.png'
            case LobbyImage.StoreItemCitiesAndKnights56P: return 'store/store_item_cities_and_knights_56player.png'
            case LobbyImage.StoreItemCitiesAndKnightsSeafarers4P: return 'store/store_item_seafarers_cities_and_knights.png'
            case LobbyImage.StoreItemCitiesAndKnightsSeafarers56P: return 'store/store_item_seafarers_cities_and_knights_56player.png'

            case LobbyImage.StoreColorBlack: return 'store_color_black.png'
            case LobbyImage.StoreColorPurple: return 'store_color_purple.png'
            case LobbyImage.StoreColorMysticBlue: return 'store_color_mysticblue.png'
            case LobbyImage.StoreColorWhite: return 'store_color_white.png'
            case LobbyImage.StoreColorBronze: return 'store_color_bronze.png'
            case LobbyImage.StoreColorSilver: return 'store_color_silver.png'
            case LobbyImage.StoreColorGold: return 'store_color_gold.png'
            case LobbyImage.StoreColorPink: return 'store_color_pink.png'

            case LobbyImage.IconSettlerHat: return 'icon_settler.svg'
            case LobbyImage.IconColonistHat: return 'icon_colonist.svg'
            case LobbyImage.IconFounderHat: return 'icon_founder.svg'
            case LobbyImage.IconChristmasHat: return 'icon_christmas_hat.svg'
            case LobbyImage.IconRobberSanta: return 'icon_robber_santa.svg'
            case LobbyImage.IconRobberLunar: return 'icon_robber_lunar.png'
            case LobbyImage.IconRobberCupid: return 'icon_robber_cupid.png'
            case LobbyImage.IconPirateShip: return 'icon_pirate_ship.svg'
            case LobbyImage.IconMedalGold: return 'medal_gold.svg'
            case LobbyImage.IconMedalSilver: return 'medal_silver.svg'
            case LobbyImage.IconMedalBronze: return 'medal_bronze.svg'

            case LobbyImage.IconElephant: return 'icon_elephant.svg'
            case LobbyImage.IconSombrero: return 'icon_sombrero.svg'
            case LobbyImage.IconFarmer: return 'icon_farmer.svg'
            case LobbyImage.IconAvocado: return 'icon_avocado.svg'
            case LobbyImage.IconCactus: return 'icon_cactus.svg'
            case LobbyImage.IconCrown: return 'icon_crown.svg'
            case LobbyImage.IconSwords: return 'icon_swords.svg'
            case LobbyImage.IconHelmet: return 'icon_helmet.svg'
            case LobbyImage.IconSnorkel: return 'icon_snorkel.svg'
            case LobbyImage.IconScarf: return 'icon_scarf.svg'
            case LobbyImage.IconTie: return 'icon_tie.svg'
            case LobbyImage.IconWorker: return 'icon_worker.svg'
            case LobbyImage.IconMummy: return 'icon_mummy.svg'
            case LobbyImage.IconGifter: return 'icon_gifter.svg'
            case LobbyImage.IconNoTroll: return 'icon_no_troll.svg'

            case LobbyImage.RoadRed: return 'road_red.svg'
            case LobbyImage.RoadBlue: return 'road_blue.svg'
            case LobbyImage.RoadGreen: return 'road_green.svg'
            case LobbyImage.RoadOrange: return 'road_orange.svg'
            case LobbyImage.RoadBlack: return 'road_black.svg'
            case LobbyImage.RoadSilver: return 'road_silver.svg'
            case LobbyImage.RoadGold: return 'road_gold.svg'
            case LobbyImage.RoadWhite: return 'road_white.svg'
            case LobbyImage.RoadPurple: return 'road_purple.svg'
            case LobbyImage.RoadMysticBlue: return 'road_mysticblue.svg'
            case LobbyImage.RoadPink: return 'road_pink.svg'

            case LobbyImage.SettlementRed: return 'settlement_red.svg'
            case LobbyImage.SettlementBlue: return 'settlement_blue.svg'
            case LobbyImage.SettlementGreen: return 'settlement_green.svg'
            case LobbyImage.SettlementOrange: return 'settlement_orange.svg'
            case LobbyImage.SettlementBlack: return 'settlement_black.svg'
            case LobbyImage.SettlementSilver: return 'settlement_silver.svg'
            case LobbyImage.SettlementGold: return 'settlement_gold.svg'
            case LobbyImage.SettlementWhite: return 'settlement_white.svg'
            case LobbyImage.SettlementPurple: return 'settlement_purple.svg'
            case LobbyImage.SettlementMysticBlue: return 'settlement_mysticblue.svg'
            case LobbyImage.SettlementPink: return 'settlement_pink.svg'

            case LobbyImage.CityRed: return 'city_red.svg'
            case LobbyImage.CityBlue: return 'city_blue.svg'
            case LobbyImage.CityGreen: return 'city_green.svg'
            case LobbyImage.CityOrange: return 'city_orange.svg'
            case LobbyImage.CityBlack: return 'city_black.svg'
            case LobbyImage.CitySilver: return 'city_silver.svg'
            case LobbyImage.CityGold: return 'city_gold.svg'
            case LobbyImage.CityWhite: return 'city_white.svg'
            case LobbyImage.CityPurple: return 'city_purple.svg'
            case LobbyImage.CityMysticBlue: return 'city_mysticblue.svg'
            case LobbyImage.CityPink: return 'city_pink.svg'

            case LobbyImage.StoreMembershipPlus: return 'membership_icon_plus.svg'
            case LobbyImage.StoreMembershipPremium: return 'membership_icon_premium.svg'
            case LobbyImage.StoreMembershipElite: return 'membership_icon_elite.svg'
            case LobbyImage.StoreMembershipHoliday2023: return 'membership_icon_holiday2023.svg'
            case LobbyImage.StoreGiftMembership: return 'icon_gift_box.svg'
            case LobbyImage.StoreGiftMembershipDark: return 'icon_gift_box_dark.svg'

            case LobbyImage.StoreMembershipPerkAllMaps: return 'perks_icon_free_maps.png'
            case LobbyImage.StoreMembershipPerkAllExpansions: return 'perks_icon_free_expansions.png'
            case LobbyImage.StoreMembershipPerkRankedGames: return 'perks_icon_ranked.png'
            case LobbyImage.StoreMembershipPerkCustomLinks: return 'perks_icon_custom_links.png'
            case LobbyImage.StoreMembershipPerkColonistCoins: return 'perks_icon_coins.png'
            case LobbyImage.StoreMembershipPerkNoAds: return 'perks_icon_no_ads.png'
            case LobbyImage.StoreMembershipPerkNoAdsGifDesertBox: return 'gif_remove_ads_boxedshape_desert.gif'
            case LobbyImage.StoreMembershipPerkNoAdsGifGoldBox: return 'gif_remove_ads_boxedshape_gold.gif'
            case LobbyImage.StoreMembershipPerkNoAdsGifDesertHex: return 'gif_remove_ads_hexedshape_desert.gif'
            case LobbyImage.StoreMembershipPerkNoAdsGifGoldHex: return 'gif_remove_ads_hexedshape_gold.gif'
            case LobbyImage.StoreMembershipPerkAllColors: return 'store_icon_colors.png'

            case LobbyImage.CoinStack1: return 'coin_stack_1.png'
            case LobbyImage.CoinStack2: return 'coin_stack_2.png'
            case LobbyImage.CoinStack3: return 'coin_stack_3.png'
            case LobbyImage.CoinStack4: return 'coin_stack_4.png'
            case LobbyImage.CoinStack5: return 'coin_stack_5.png'
            case LobbyImage.CoinStack6: return 'coin_stack_6.png'

            case LobbyImage.Friends: return 'icon_profile_friends.svg'

            case LobbyImage.GoogleIcon: return 'icon_google.svg'
            case LobbyImage.AppleIcon: return 'icon_apple.svg'
            case LobbyImage.DiscordIcon: return 'icon_discord.svg'
            case LobbyImage.FacebookIcon: return 'icon_facebook.svg'

            case LobbyImage.RankBronze: return `${this.STATIC_RANKED_DIVISION_PATH}/bronze.svg`
            case LobbyImage.RankSilver: return `${this.STATIC_RANKED_DIVISION_PATH}/silver.svg`
            case LobbyImage.RankGold: return `${this.STATIC_RANKED_DIVISION_PATH}/gold.svg`
            case LobbyImage.RankPlatinum: return `${this.STATIC_RANKED_DIVISION_PATH}/platinum.svg`
            case LobbyImage.RankDiamond: return `${this.STATIC_RANKED_DIVISION_PATH}/diamond.svg`
            case LobbyImage.RankMaster: return `${this.STATIC_RANKED_DIVISION_PATH}/master.svg`
            case LobbyImage.RankGrandmaster: return `${this.STATIC_RANKED_DIVISION_PATH}/grandmaster.svg`

            case LobbyImage.RankSubdivisionI: return `${this.STATIC_RANKED_DIVISION_PATH}/subdivisions_i.svg`
            case LobbyImage.RankSubdivisionII: return `${this.STATIC_RANKED_DIVISION_PATH}/subdivisions_ii.svg`
            case LobbyImage.RankSubdivisionIII: return `${this.STATIC_RANKED_DIVISION_PATH}/subdivisions_iii.svg`
            case LobbyImage.RankSubdivisionIV: return `${this.STATIC_RANKED_DIVISION_PATH}/subdivisions_iv.svg`
            case LobbyImage.RankSubdivisionV: return `${this.STATIC_RANKED_DIVISION_PATH}/subdivisions_v.svg`
            case LobbyImage.LandscapeIcon: return 'icon_landscape_orientation.svg'
            case LobbyImage.PortraitIcon: return 'icon_portrait_orientation.svg'

            case LobbyImage.RewindToStart: return 'icon_rewind_start.svg'
            case LobbyImage.Rewind: return 'icon_rewind.svg'
            case LobbyImage.Play: return 'icon_play.svg'
            case LobbyImage.Pause: return 'icon_pause.svg'
            case LobbyImage.FastForward: return 'icon_forward.svg'
            case LobbyImage.FastForwardToEnd: return 'icon_forward_end.svg'

            case LobbyImage.X05: return 'icon_05x.svg'
            case LobbyImage.X1: return 'icon_1x.svg'
            case LobbyImage.X2: return 'icon_2x.svg'
            case LobbyImage.X4: return 'icon_4x.svg'
            case LobbyImage.X8: return 'icon_8x.svg'

            case LobbyImage.DownArrow: return 'icon_arrow_down.svg'
            case LobbyImage.UpArrow: return 'icon_arrow_up.svg'

            case LobbyImage.LongestRoad: return 'icon_longest_road_highlight.svg'
            case LobbyImage.LargestArmy: return 'icon_largest_army_highlight.svg'
            case LobbyImage.CardMonopoly: return 'card_monopoly.svg'
            case LobbyImage.CardVictoryPoint: return 'card_vp.svg'

            case LobbyImage.ProfileOverview: return 'icon_profile_overview.svg'
            case LobbyImage.ProfileRanked: return 'icon_profile_ranked.svg'
            case LobbyImage.ProfileHistory: return 'icon_profile_history.svg'
            case LobbyImage.ProfileItems: return 'icon_profile_items.svg'
            case LobbyImage.ProfileSettings: return 'icon_settings.svg'

            case LobbyImage.TournamentS11First: return 'badge_tournament_s11_first.svg'
            case LobbyImage.Tournament4P: return 'badge_tournament_4p.svg'

            case LobbyImage.BannerMember: return 'banner_member.png'
        }
    }
}
